import { ClientResponseError } from "pocketbase";
import {
  createContext,
  startTransition,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { ReactNode } from "react";
import i18n, { isLocale, type Locale } from "@/i18n";
import { nameParts } from "@/lib/api/users";
import type { UsersResponse } from "@/lib/core/pb-types";
import { getAttribution } from "@/lib/analytics/attribution";
import { TERMS_VERSION } from "./legal";
import { pb } from "@/lib/core/pocketbase";
import { takePowSolution } from "./pow";

export type AuthUser = UsersResponse;

type RegisterInput = { email: string; password: string; name: string; locale?: Locale };

type AuthValue = {
  user: AuthUser | null;
  isAuthenticated: boolean;
  /** True until the persisted session has been validated against the server. */
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  /**
   * Signs in (or up) with Google, Apple or Microsoft in a popup. A new account accepts the current
   * terms and gets the visitor's language and attribution, as a password sign-up does.
   */
  loginWithOAuth: (provider: string) => Promise<void>;
  /** Creates the account; the user can log in after confirming the verification email. */
  register: (input: RegisterInput) => Promise<void>;
  resendVerification: (email: string) => Promise<void>;
  requestPasswordReset: (email: string) => Promise<void>;
  logout: () => void;
  /** Switches the UI language and stores it on the user record when signed in. */
  setLocale: (locale: Locale) => Promise<void>;
};

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  // A stored record whose token has expired is no session: requests would go out anonymous.
  const [user, setUser] = useState<AuthUser | null>(() =>
    pb.authStore.isValid ? ((pb.authStore.record as AuthUser | null) ?? null) : null,
  );
  const [loading, setLoading] = useState(pb.authStore.isValid);

  useEffect(() => {
    const unsubscribe = pb.authStore.onChange((_token, record) => {
      setUser((record as AuthUser | null) ?? null);
    });

    // Validate (and refresh) a persisted token on startup. Only the server's "no" ends the
    // session: offline, a restart (5xx) or a rate limit keep it, checked again once back online.
    const refresh = () =>
      pb
        .collection("users")
        .authRefresh()
        .then(
          () => undefined,
          (error: unknown) => {
            if (error instanceof ClientResponseError && [401, 403, 404].includes(error.status)) {
              pb.authStore.clear();
            } else {
              window.addEventListener("online", refresh, { once: true });
            }
          },
        );
    // An expired token cannot be refreshed: the session ends here, and the login page follows.
    const check = () => {
      if (pb.authStore.isValid) return refresh();
      if (pb.authStore.record) pb.authStore.clear();
      return Promise.resolve();
    };
    void check().finally(() => setLoading(false));
    // A tab (or the installed app) left open for days checks again when it comes back.
    const onVisible = () => {
      if (document.visibilityState === "visible") void check();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      unsubscribe();
      window.removeEventListener("online", refresh);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  // A signed-in user's saved language wins over the browser's.
  useEffect(() => {
    if (user && isLocale(user.locale) && user.locale !== i18n.resolvedLanguage) {
      void i18n.changeLanguage(user.locale);
    }
  }, [user]);

  const login = useCallback(async (email: string, password: string) => {
    await pb.collection("users").authWithPassword(email, password);
  }, []);

  const loginWithOAuth = useCallback(async (provider: string) => {
    // The popup opens right in the click (or browsers block it) and is watched: closing it ends
    // the wait with an abort error. The SDK cannot cancel by itself here (auto-cancellation is
    // off), so its realtime listener stays until the next page load; harmless.
    const features = "width=520,height=680";
    let popup = window.open("", "blanqo-oauth", features);
    let watch = 0;
    const closed = new Promise<never>((_, reject) => {
      watch = window.setInterval(() => {
        if (popup?.closed) reject(new ClientResponseError({ isAbort: true, message: "closed" }));
      }, 500);
    });
    try {
      await Promise.race([
        pb.collection("users").authWithOAuth2({
          provider,
          urlCallback: (url) => {
            if (popup && !popup.closed) popup.location.href = url;
            else popup = window.open(url, "blanqo-oauth", features);
          },
          createData: {
            locale: isLocale(i18n.resolvedLanguage) ? i18n.resolvedLanguage : "en",
            termsVersion: TERMS_VERSION,
            attribution: getAttribution(),
          },
        }),
        closed,
      ]);
    } finally {
      window.clearInterval(watch);
      if (popup && !popup.closed) popup.close();
    }
  }, []);

  const register = useCallback(async ({ email, password, name, locale }: RegisterInput) => {
    // The signup hook creates the workspace and sends the verification email
    // (server/pb_hooks); logging in only works once the email is confirmed. The proof of work
    // (lib/auth/pow.ts) is usually solved already, while the visitor filled in the form.
    const pow = await takePowSolution();
    await pb.collection("users").create(
      {
        email,
        password,
        passwordConfirm: password,
        name: name.trim(),
        ...nameParts({ name }),
        // English unless the visitor picked (or browses in) another supported language.
        locale: locale ?? (isLocale(i18n.resolvedLanguage) ? i18n.resolvedLanguage : "en"),
        // The form only submits once the terms are accepted.
        termsVersion: TERMS_VERSION,
        // Where this visitor first came from (campaign, referral, landing page).
        attribution: getAttribution(),
      },
      { headers: { "X-Blanqo-Pow": pow } },
    );
  }, []);

  const resendVerification = useCallback(async (email: string) => {
    await pb.collection("users").requestVerification(email);
  }, []);

  const requestPasswordReset = useCallback(async (email: string) => {
    await pb.collection("users").requestPasswordReset(email);
  }, []);

  const logout = useCallback(() => {
    // React Router runs navigate() as a transition; clearing the store inside one too keeps a
    // `navigate("/"); logout();` pair in a single render instead of letting a route guard win.
    startTransition(() => pb.authStore.clear());
  }, []);

  const setLocale = useCallback(
    async (locale: Locale) => {
      await i18n.changeLanguage(locale);
      if (user && user.locale !== locale) {
        await pb.collection("users").update(user.id, { locale });
      }
    },
    [user],
  );

  const value = useMemo<AuthValue>(
    () => ({
      user,
      isAuthenticated: user !== null,
      loading,
      login,
      loginWithOAuth,
      register,
      resendVerification,
      requestPasswordReset,
      logout,
      setLocale,
    }),
    [
      user,
      loading,
      login,
      loginWithOAuth,
      register,
      resendVerification,
      requestPasswordReset,
      logout,
      setLocale,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
